import { create } from 'zustand'

type DeleteAction = () => Promise<unknown> | unknown

type DeleteConfirmationRequest = {
  count: number
  entity?: 'time entry' | 'activity'
  workItemCountInEntry?: number
  action?: DeleteAction
  resolve: (confirmed: boolean) => void
  reject: (error: unknown) => void
}

type DeleteTimeEntryConfirmationState = {
  request: DeleteConfirmationRequest | null
  deleting: boolean
  cancel: () => void
  confirm: () => Promise<void>
  open: (request: DeleteConfirmationRequest) => void
}

export const useDeleteTimeEntryConfirmationStore =
  create<DeleteTimeEntryConfirmationState>((set, get) => ({
    request: null,
    deleting: false,
    open: request => {
      if (get().deleting) {
        request.resolve(false)
        return
      }
      get().request?.resolve(false)
      set({ request })
    },
    cancel: () => {
      const { request, deleting } = get()
      if (!request || deleting) return

      set({ request: null })
      request.resolve(false)
    },
    confirm: async () => {
      const { request, deleting } = get()
      if (!request || deleting) return

      if (!request.action) {
        set({ request: null })
        request.resolve(true)
        return
      }

      // Keep the dialog open with the Delete button loading until the
      // deletion finishes.
      set({ deleting: true })
      try {
        await request.action()
        set({ request: null, deleting: false })
        request.resolve(true)
      } catch (error) {
        set({ request: null, deleting: false })
        request.reject(error)
      }
    },
  }))

/**
 * Ask the user to confirm deleting time entries.
 *
 * Direct time entry deletes use this for consistent dialog copy. Deletion
 * already confirmed by the email Block dialog skips this extra prompt.
 * Pass `action` to run the deletion while
 * the dialog shows a loading Delete button; the promise then resolves `true`
 * after it succeeds, `false` if cancelled, and rejects with the action's error.
 */
export const confirmTimeEntryDeletion = (
  count = 1,
  action?: DeleteAction,
  options?: {
    entity?: 'time entry' | 'activity'
    workItemCountInEntry?: number
  }
) =>
  new Promise<boolean>((resolve, reject) => {
    useDeleteTimeEntryConfirmationStore.getState().open({
      count: Math.max(1, count),
      ...options,
      action,
      resolve,
      reject,
    })
  })
